import React, {
  useReducer,
  useRef,
  useState,
  useEffect,
  memo,
  useCallback,
} from 'react';
import {
  Tabs,
  Table,
  Input,
  Row,
  Col,
  Checkbox,
  Button,
  Modal,
  message,
} from 'antd';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const { TextArea } = Input;
const STORAGE_KEY = 'tabbedTablesDataV2';

// A stable Field component defined at top-level so it won't remount on each render
const Field = memo(({ label, value, onChange, placeholder }) => (
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
    <span style={{ width: 200, fontWeight: 'normal' }}>{label}:</span>
    <Input
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{ flex: 1 }}
    />
  </div>
));

const initialState = {
  general: {
    date: '',
    companyName: '',
    registrationNumber: '',
    businessActivities1: '',
    businessActivities2: '',
    shareholders: '',
    foreignInvestmentStructure: '',
    politicallyExposedPerson: { yes: false, no: false },
    hasPoliticallyExposedPerson: { yes: false, no: false },
    companyEstablishedDate: '',
    address: '',
    hasSubdivision: { yes: false, no: false },
    phone: '',
    hasBranches: { yes: false, no: false },
    branchCount: '',
    employeeCount: '',
    hasAccountant: { yes: false, no: false },
    accountantType: { permanent: false, contract: false },
    hasFinancialSoftware: { yes: false, no: false },
    financialSoftwareName: '',
    annualRevenue: {
      range1: false,
      range2: false,
      range3: false,
      range4: false,
    },
    taxInspection: { yes: false, no: false },
    taxInspectionViolation: { yes: false, no: false },
    taxInspectionDate: '',
    violationDetails: '',
    serviceType: {
      internalAudit: false,
      consulting: false,
      contractAccounting: false,
    },
    reasonForTaxConsulting: '',
    clientIssue1: '',
    clientIssue2: '',
    clientIssue3: '',
    clientIssue4: '',
    noteTaker: '',
    taxConsultant: '',
    taxConsultantCode: '',
    User1rank: '',
    User1name: '',
    User2rank: '',
    User2name: '',
    reportPreparedBy: '',
    contractNumber: '',
    workPeriod: '',
    reviewerCompany: '',
  },
  tables: {
    table1: Array.from({ length: 5 }, (_, i) => ({
      key: `${i + 1}`,
      number: `${i + 1}`,
      work: '',
      note: '',
    })),
    table2: [
      { key: '1', number: '1', recommendation: '', result: '' },
      { key: '2', number: '2', recommendation: '', result: '' },
      { key: '3', number: '3', recommendation: '', result: '' },
      { key: '4', number: '4', recommendation: '', result: '' },
      { key: '5', number: '5', recommendation: '', result: '' },
    ],
    table3: Array.from({ length: 5 }, (_, i) => ({
      key: `${i + 1}`,
      number: `${i + 1}`,
      content: '',
      note: '',
    })),
  },
};

function deepCopy(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return {
        ...state,
        general: { ...state.general, [action.field]: action.value },
      };
    case 'SET_NESTED_FIELD':
      return {
        ...state,
        general: {
          ...state.general,
          [action.field]: { ...state.general[action.field], ...action.value },
        },
      };
    case 'SET_TABLE_CELL':
      return {
        ...state,
        tables: {
          ...state.tables,
          [action.table]: state.tables[action.table].map((row) =>
            row.key === action.key
              ? { ...row, [action.col]: action.value }
              : row
          ),
        },
      };
    case 'SET_STATE':
      return deepCopy(action.value);
    default:
      return state;
  }
}

function EditableCellExpand({
  value,
  onChange,
  inputType,
}) {
  const textAreaRef = useRef(null);

  // Auto-resize logic
  useEffect(() => {
    if (inputType === 'textarea' && textAreaRef.current) {
      const textArea = textAreaRef.current.resizableTextArea.textArea;
      textArea.style.height = 'auto'; // Reset height to calculate scrollHeight
      textArea.style.height = `${textArea.scrollHeight}px`; // Set to content height
    }
  }, [value, inputType]);

  return (
    <div
      className="editable-cell-wrapper"
      style={{
        position: 'relative',
        width: '100%',
        overflow: 'visible',
      }}
    >
      {inputType === 'textarea' ? (
        <TextArea
          ref={textAreaRef}
          value={value}
          onChange={onChange}
          style={{
            width: '100%',
            resize: 'none',
            minHeight: '38px',
            transition: 'min-height 0.1s',
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            lineHeight: '1.4',
            padding: '4px 8px',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
          }}
        />
      ) : (
        <Input value={value} onChange={onChange} />
      )}
    </div>
  );
}

export default function TabbedTables() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const pendingTab = useRef(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Load saved data once
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        dispatch({ type: 'SET_STATE', value: JSON.parse(saved) });
      } catch {}
    }
  }, []);

  const addRowToTable1 = () => {
    const newKey = `${state.tables.table1.length + 1}`;
    const newRow = { key: newKey, number: newKey, work: '', note: '' };
    dispatch({
      type: 'SET_STATE',
      value: {
        ...state,
        tables: {
          ...state.tables,
          table1: [...state.tables.table1, newRow],
        },
      },
    });
  };

  const addRowToTable2 = () => {
    const newKey = `${state.tables.table2.length + 1}`;
    const newRow = { key: newKey, number: newKey, recommendation: '', result: '' };
    dispatch({
      type: 'SET_STATE',
      value: {
        ...state,
        tables: {
          ...state.tables,
          table2: [...state.tables.table2, newRow],
        },
      },
    });
  };

  const addRowToTable3 = () => {
    const newKey = `${state.tables.table3.length + 1}`;
    const newRow = { key: newKey, number: newKey, content: '', note: '' };
    dispatch({
      type: 'SET_STATE',
      value: {
        ...state,
        tables: {
          ...state.tables,
          table3: [...state.tables.table3, newRow],
        },
      },
    });
  };

  const handleGeneralChange = useCallback((field, value) => {
    setDirty(true);
    dispatch({ type: 'SET_FIELD', field, value });
  }, []);

  const handleTableChange = useCallback((table, key, col, value) => {
    setDirty(true);
    dispatch({ type: 'SET_TABLE_CELL', table, key, col, value });
  }, []);

  const saveToLocalStorage = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    setDirty(false);
    message.success('Data saved!');
  };

  const onTabChange = (key) => {
    if (dirty) {
      pendingTab.current = key;
      setModalVisible(true);
    } else {
      setActiveTab(key);
    }
  };

  const handleModalOk = () => {
    saveToLocalStorage();
    setActiveTab(pendingTab.current);
    setModalVisible(false);
  };

  const handleModalCancel = () => {
    setActiveTab(pendingTab.current);
    setModalVisible(false);
    setDirty(false);
  };

  const handlePrint = (tabKey) => {
    const titleMap = {
      1: 'ГҮЙЦЭТГЭСЭН АЖЛЫН ТАЙЛАН',
      2: 'ТАТВАРЫН МЭРГЭШСЭН ЗӨВЛӨХ ҮЙЛЧИЛГЭЭНИЙ ТАЙЛАН',
      3: 'УУЛЗАЛТЫН ТЭМДЭГЛЭЛ',
    };

    const base64Logo =
      'data:image/png;base64,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';

    // Generate table HTML with static text
    let tableData;
    let headers;
    if (tabKey === '1') {
      tableData = state.tables.table1;
      headers = ['№', 'ГҮЙЦЭТГЭСЭН АЖЛУУД', 'ТЭМДЭГЛЭЛ'];
    } else if (tabKey === '2') {
      tableData = state.tables.table2;
      headers = ['№', 'ЗӨВЛӨМЖ', 'ХҮРЭХ ҮР ДҮН'];
    } else {
      tableData = state.tables.table3;
      headers = ['№', 'АГУУЛГА', 'ТЭМДЭГЛЭЛ'];
    }

    const tableRows = tableData.map(row => `
      <tr>
        <td>${row.number}</td>
        <td><span class="cell-content">${row.work || row.recommendation || row.content || ''}</span></td>
        <td><span class="cell-content">${row.note || row.result || ''}</span></td>
      </tr>
    `).join('');

    const tableHtml = `
      <table id="table${tabKey}">
        <thead>
          <tr>
            ${headers.map(header => `<th>${header}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
    `;

    // Include general fields for specific tabs
    let generalFieldsHtml = '';
    let generalFieldsHtml2 = '';
    if (tabKey === '1') {
      generalFieldsHtml = `
        <div class="general-fields">
          <div><span class="field-label">Компанийн нэр:</span> <span>${state.general.companyName || ''}</span></div>
          <div><span class="field-label">Гэрээний дугаар:</span> <span>${state.general.contractNumber || ''}</span></div>
          <div><span class="field-label">Ажил гүйцэтгэсэн хугацаа:</span> <span>${state.general.workPeriod || ''}</span></div>
        </div>
      `;
      generalFieldsHtml2 = `
        <div class="general-fields">
          <div style="margin-top: 20px;">
            <h4 style="font-weight: bold; font-size: 16px;">ТМЗ үйлчилгээний дараах алхамууд</h4>
          </div>
          <div><span class="field-label">Тайлан гаргасан:</span> <span>${state.general.reportPreparedBy || ''}</span></div>
          <div><span class="field-label">Татварын мэргэшсэн зөвлөх:</span> <span>${state.general.taxConsultant || ''}</span></div>
          <div><span class="field-label">Тайланг хянан баталсан:</span></div>
          <div class="compact-signature">
            <div class="field-label"><span>${state.general.User1rank || ''}</span></div>
            <div class="field-label"><span>${state.general.User1name || ''}</span></div>
            <div class="signature-box">Гарын үсэг</div>
          </div>
          <div class="compact-signature">
            <div class="field-label"><span>${state.general.User2rank || ''}</span></div>
            <div class="field-label"><span>${state.general.User2name || ''}</span></div>
            <div class="signature-box">Гарын үсэг</div>
          </div>
        </div>`;
    } else if (tabKey === '2') {
      generalFieldsHtml = `
        <div class="general-fields">
          <div><span class="field-label">Компанийн нэр:</span> <span>${state.general.companyName || ''}</span></div>
          <div><span class="field-label">Огноо:</span> <span>${state.general.date || ''}</span></div>
        </div>
      `;
      generalFieldsHtml2 = `
        <div class="general-fields">
          <div><span class="field-label">Татварын мэргэшсэн зөвлөх:</span> <span>${state.general.taxConsultant || ''}</span></div>
          <div><span class="field-label">ТМЗ код:</span> <span>${state.general.taxConsultantCode || ''}</span></div>
          <div><span class="field-label">Хүлээн авсан</span></div>
          <div class="compact-signature">
            <div class="field-label"><span>${state.general.User1rank || ''}</span></div>
            <div class="field-label"><span>${state.general.User1name || ''}</span></div>
            <div class="signature-box">Гарын үсэг</div>
          </div>
          <div class="compact-signature">
            <div class="field-label"><span>${state.general.User2rank || ''}</span></div>
            <div class="field-label"><span>${state.general.User2name || ''}</span></div>
            <div class="signature-box">Гарын үсэг</div>
          </div>
        </div>`;
    } else if (tabKey === '3') {
      generalFieldsHtml = `
        <div class="general-fields">
          <div><span class="field-label">Огноо:</span> <span>${state.general.date || ''}</span></div>
          <div><span class="field-label">Компанийн нэр:</span> <span>${state.general.companyName || ''}</span></div>
          <div><span class="field-label">Регистрийн дугаар:</span> <span>${state.general.registrationNumber || ''}</span></div>
          <div><span class="field-label">Үйл ажиллагааны чиглэл 1:</span> <span>${state.general.businessActivities1 || ''}</span></div>
          <div><span class="field-label">Үйл ажиллагааны чиглэл 2:</span> <span>${state.general.businessActivities2 || ''}</span></div>
          <div><span class="field-label">Хувь нийлүүлэгчид:</span> <span>${state.general.shareholders || ''}</span></div>
          <div><span class="field-label">Гадаад хөрөнгө оруулалтын бүтэц:</span> <span>${state.general.foreignInvestmentStructure || ''}</span></div>
          <div><span class="field-label">Улс төрийн нөлөө бүхий хүн:</span> <span>${state.general.politicallyExposedPerson?.yes ? 'Тийм' : state.general.politicallyExposedPerson?.no ? 'Үгүй' : ''}</span></div>
          <div><span class="field-label">Компани байгуулагдсан огноо:</span> <span>${state.general.companyEstablishedDate || ''}</span></div>
          <div>
          <span class="field-label">Хаяг:</span> <span>${state.general.address || ''}</span>
          <span class="field-label">Утас:</span> <span>${state.general.phone || ''}</span>
          </div>
          <div><span class="field-label">Салбартай эсэх:</span> <span>${state.general.hasBranches?.yes ? 'Тийм' : state.general.hasBranches?.no ? 'Үгүй' : ''}</span>
              <span class="field-label">Салбарын тоо:</span> <span>${state.general.branchCount || ''}</span>
              <span class="field-label">Ажилчдын тоо:</span> <span>${state.general.employeeCount || ''}</span>
          </div>
          <div><span class="field-label">Нягтлан бодогчтой эсэх:</span> <span>${state.general.hasAccountant?.yes ? 'Тийм' : state.general.hasAccountant?.no ? 'Үгүй' : ''}</span></div>
          <div><span class="field-label">Нягтлан бодогч нь:</span> <span>${state.general.accountantType?.permanent ? 'Үндсэн' : state.general.accountantType?.contract ? 'Гэрээт' : ''}</span></div>
          <div>
            <span class="field-label">Санхүүгийн программтай эсэх:</span> <span>${state.general.hasFinancialSoftware?.yes ? 'Тийм' : state.general.hasFinancialSoftware?.no ? 'Үгүй' : ''}</span>
            <span class="field-label">Санхүүгийн программын нэр:</span> <span>${state.general.financialSoftwareName || ''}</span>
          </div>
          <div><span class="field-label">Жилийн борлуулалтын орлого:</span> <span>
            ${state.general.annualRevenue?.range1 ? '50 сая - 300 сая' : ''}
            ${state.general.annualRevenue?.range2 ? '300 сая - 1.5 тэрбум' : ''}
            ${state.general.annualRevenue?.range3 ? '1.5 тэрбум - 6 тэрбум' : ''}
            ${state.general.annualRevenue?.range4 ? '6 тэрбум болон дээш' : ''}
          </span></div>
          <div><span class="field-label">Татварын хяналт шалгалт орсон эсэх:</span> <span>${state.general.taxInspection?.yes ? 'Тийм' : state.general.taxInspection?.no ? 'Үгүй' : ''}</span></div>
          <div><span class="field-label">Шалгалтаар зөрчил илэрсэн эсэх:</span> <span>${state.general.taxInspectionViolation?.yes ? 'Тийм' : state.general.taxInspectionViolation?.no ? 'Үгүй' : ''}</span></div>
          <div><span class="field-label">Татварын хяналт шалгалтаар орсон огноо:</span> <span>${state.general.taxInspectionDate || ''}</span></div>
          <div><span class="field-label">Илэрсэн зөрчлийн мэдээлэл:</span> <span class="cell-content">${state.general.violationDetails || ''}</span></div>
          <div><span class="field-label">Үйлчилгээний төрөл:</span> <span>
            ${state.general.serviceType?.internalAudit ? 'Санхүүгийн дотоод' : ''}
            ${state.general.serviceType?.consulting ? 'Зөвлөх үйлчилгээ' : ''}
            ${state.general.serviceType?.contractAccounting ? 'Гэрээт нягтлан' : ''}
          </span></div>
        </div>
      `;
      generalFieldsHtml2 = `
        <div class="general-fields">
          <div><span class="field-label">Тэмдэглэл хөтөлсөн:</span> <span>${state.general.noteTaker || ''}</span></div>
        </div>`;
    }

    const w = window.open('', '_blank');
    w.document.write(`
      <html>
        <head>
          <title>Хэвлэх</title>
          <style>
            body {
              font-family: 'Noto Sans Mongolian', Arial, sans-serif;
              padding: 20px;
              font-size: 12px;
              line-height: 1.4;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              font-size: 10px;
              table-layout: fixed;
            }
            th, td {
              border: 2px solid #000;
              padding: 8px;
              text-align: left;
              vertical-align: top;
              overflow: visible;
            }
            th {
              background: #f2f2f2;
              font-weight: bold;
            }
            table#table${tabKey} th:nth-child(1), table#table${tabKey} td:nth-child(1) {
              width: ${tabKey === '2' ? '5%' : '5%'};
              text-align: center;
            }
            table#table${tabKey} th:nth-child(2), table#table${tabKey} td:nth-child(2) {
              width: ${tabKey === '2' ? '35%' : '55%'};
            }
            table#table${tabKey} th:nth-child(3), table#table${tabKey} td:nth-child(3) {
              width: ${tabKey === '2' ? '60%' : '40%'};
            }
            .cell-content {
              white-space: pre-wrap;
              line-height: 1.4;
              display: block;
              width: 100%;
              box-sizing: border-box;
            }
            .general-fields {
              margin-bottom: 20px;
            }
            .general-fields div {
              margin-bottom: 8px;
            }
            .field-label {
              font-weight: bold;
              margin-right: 10px;
              font-size: 12px;
            }
            .ant-input, .ant-input-textarea textarea, .ant-btn, .header-buttons {
              display: none;
            }
            .span {
              font-weight: normal !important;
            }
            .print-header {
              display: flex;
              align-items: center;
              margin-bottom: 20px;
            }
            .print-header img {
              height: 60px;
              margin-right: 20px;
            }
            .print-header-title {
              font-size: 16px;
              font-weight: bold;
            }
            .print-footer {
              margin-top: 20px;
            }
            .compact-signature {
              display: flex;
              gap: 10px;
              align-items: center;
              margin-top: 20px;
              font-size: 12px;
            }
            .signature-block {
              display: flex;
              justify-content: space-between;
              margin-top: 20px;
            }
            .signature-box {
              width: 45%;
              border-top: 1px solid #000;
              text-align: center;
              padding-top: 4px;
              font-size: 12px;
            }
            .stamp-box {
              width: 45%;
              text-align: right;
              font-size: 12px;
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="print-header-title">${titleMap[tabKey]}</div>
          </div>
          ${generalFieldsHtml}
          ${tableHtml}
          ${generalFieldsHtml2}
          <div class="print-footer">
            <div class="signature-block">
               
              <div class="stamp-box">Тамга</div>
            </div>
          </div>
        </body>
      </html>
    `);
    w.document.close();
    w.onload = () => w.print();
  };

  const handleExportToPDF = (tabKey, title) => {
    try {
      const doc = new jsPDF();
      doc.setFont('helvetica');
      doc.setFontSize(12);
      let y = 10;
      doc.text(title, 10, y);
      y += 10;
      let cols = [];
      let rows = [];
      if (tabKey === '1') {
        cols = ['№', 'ГҮЙЦЭТГЭСЭН АЖЛУУД', 'ТЭМДЭГЛЭЛ'];
        rows = state.tables.table1.map((r) => [r.number, r.work, r.note]);
      } else if (tabKey === '2') {
        cols = ['№', 'ЗӨВЛӨМЖ', 'ХҮРЭХ ҮР ДҮН'];
        rows = state.tables.table2.map((r) => [r.number, r.recommendation, r.result]);
      } else {
        cols = ['№', 'АГУУЛГА', 'ТЭМДЭГЛЭЛ'];
        rows = state.tables.table3.map((r) => [r.number, r.content, r.note]);
      }
      doc.autoTable({
        startY: y,
        head: [cols],
        body: rows,
        styles: { fontSize: 10 },
      });
      doc.save(`${title}.pdf`);
    } catch {
      message.error('Failed to generate PDF');
    }
  };

  const table1Cols = [
    { title: '№', dataIndex: 'number', width: 50, align: 'center' },
    {
      title: 'ГҮЙЦЭТГЭСЭН АЖЛУУД',
      dataIndex: 'work',
      width: 350,
      key: 'work',
      render: (text, rec) => (
        <EditableCellExpand
          value={text}
          onChange={(e) =>
            handleTableChange('table1', rec.key, 'work', e.target.value)
          }
          inputType="textarea"
        />
      ),
    },
    {
      title: 'ТЭМДЭГЛЭЛ',
      dataIndex: 'note',
      key: 'note',
      render: (text, rec) => (
        <EditableCellExpand
          value={text}
          onChange={(e) =>
            handleTableChange('table1', rec.key, 'note', e.target.value)
          }
          inputType="textarea"
        />
      ),
    },
  ];

  const table2Cols = [
    { title: '№', dataIndex: 'number', width: 50, align: 'center' },
    {
      title: 'ЗӨВЛӨМЖ',
      width: 350,
      dataIndex: 'recommendation',
      key: 'recommendation',
      render: (text, rec) => (
        <EditableCellExpand
          value={text}
          onChange={(e) =>
            handleTableChange('table2', rec.key, 'recommendation', e.target.value)
          }
          inputType="textarea"
        />
      ),
    },
    {
      title: 'ХҮРЭХ ҮР ДҮН',
      dataIndex: 'result',
      key: 'result',
      render: (text, rec) => (
        <EditableCellExpand
          value={text}
          onChange={(e) =>
            handleTableChange('table2', rec.key, 'result', e.target.value)
          }
          inputType="textarea"
        />
      ),
    },
  ];

  const table3Cols = [
    { title: '№', dataIndex: 'number', width: 50, align: 'center' },
    {
      title: 'АГУУЛГА',
      width: 350,
      dataIndex: 'content',
      key: 'content',
      render: (text, rec) => (
        <EditableCellExpand
          value={text}
          onChange={(e) =>
            handleTableChange('table3', rec.key, 'content', e.target.value)
          }
          inputType="textarea"
        />
      ),
    },
    {
      title: 'ТЭМДЭГЛЭЛ',
      dataIndex: 'note',
      key: 'note',
      render: (text, rec) => (
        <EditableCellExpand
          value={text}
          onChange={(e) =>
            handleTableChange('table3', rec.key, 'note', e.target.value)
          }
          inputType="textarea"
        />
      ),
    },
  ];

  const g = state.general;

  const YesNoCheckbox = ({ field, labelYes = 'Тийм', labelNo = 'Үгүй' }) => {
    const value = g[field] || { yes: false, no: false };
    return (
      <>
        <Checkbox
          checked={value.yes}
          onChange={(e) => {
            setDirty(true);
            dispatch({
              type: 'SET_NESTED_FIELD',
              field,
              value: { yes: e.target.checked, no: !e.target.checked },
            });
          }}
        >
          {labelYes}
        </Checkbox>
        <Checkbox
          checked={value.no}
          onChange={(e) => {
            setDirty(true);
            dispatch({
              type: 'SET_NESTED_FIELD',
              field,
              value: { yes: !e.target.checked, no: e.target.checked },
            });
          }}
          style={{ marginLeft: 20 }}
        >
          {labelNo}
        </Checkbox>
      </>
    );
  };

  const AccountantCheckbox = ({
    field,
    labelYes = 'Үндсэн',
    labelNo = 'Гэрээт',
  }) => {
    const value = g[field] || { permanent: false, contract: false };
    return (
      <>
        <Checkbox
          checked={value.permanent}
          onChange={(e) => {
            setDirty(true);
            dispatch({
              type: 'SET_NESTED_FIELD',
              field,
              value: { permanent: e.target.checked, contract: !e.target.checked },
            });
          }}
        >
          {labelYes}
        </Checkbox>
        <Checkbox
          checked={value.contract}
          onChange={(e) => {
            setDirty(true);
            dispatch({
              type: 'SET_NESTED_FIELD',
              field,
              value: { permanent: !e.target.checked, contract: e.target.checked },
            });
          }}
          style={{ marginLeft: 20 }}
        >
          {labelNo}
        </Checkbox>
      </>
    );
  };

  const tabItems = [
    {
      key: '3',
      label: 'УУЛЗАЛТЫН ТЭМДЭГЛЭЛ',
      children: (
        <div id="tab-content-3">
          <div className="header-buttons">
            <Button className="add-btn" onClick={() => handlePrint('3')}>
              Хэвлэх
            </Button>
            <Button className="add-btn" onClick={saveToLocalStorage}>
              Хадгалах
            </Button>
          </div>
          <Row gutter={[16, 16]} style={{ margin: 20 }}>
            <Col span={24}>
              <Field
                label="Огноо"
                value={g.date}
                onChange={(e) => handleGeneralChange('date', e.target.value)}
              />
            </Col>
            <Col span={24}>
              <Field
                label="Компанийн нэр"
                value={g.companyName}
                onChange={(e) => handleGeneralChange('companyName', e.target.value)}
              />
            </Col>
            <Col span={24}>
              <Field
                label="Регистрийн дугаар"
                value={g.registrationNumber}
                onChange={(e) =>
                  handleGeneralChange('registrationNumber', e.target.value)
                }
              />
            </Col>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ width: 150, fontWeight: 'bold' }}>
                  Үйл ажиллагааны чиглэл:
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ width: 30 }}>1.</span>
                    <Input
                      value={g.businessActivities1}
                      onChange={(e) =>
                        handleGeneralChange('businessActivities1', e.target.value)
                      }
                      style={{ flex: 1 }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ width: 30 }}>2.</span>
                    <Input
                      value={g.businessActivities2}
                      onChange={(e) =>
                        handleGeneralChange('businessActivities2', e.target.value)
                      }
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
              </div>
            </Col>
            <Col span={24}>
              <Field
                label="Хувь нийлүүлэгчид"
                value={g.shareholders}
                onChange={(e) => handleGeneralChange('shareholders', e.target.value)}
              />
            </Col>
            <Col span={24}>
              <Field
                label="Гадаад хөрөнгө оруулалтын бүтэц /хөрөнгө оруулагчдын мэдээлэл/"
                value={g.foreignInvestmentStructure}
                onChange={(e) =>
                  handleGeneralChange('foreignInvestmentStructure', e.target.value)
                }
              />
            </Col>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ width: 150, fontWeight: 'bold' }}>
                  Хувьцаа эзэмшигч болон гүйцэтгэх удирдлагад улс төрийн нөлөө
                  бүхий хүн байгаа эсэх
                </span>
                <YesNoCheckbox field="politicallyExposedPerson" />
              </div>
            </Col>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ width: 150, fontWeight: 'bold' }}>
                  Компани байгуулагдсан огноо:
                </span>
                <Input
                  value={g.companyEstablishedDate}
                  onChange={(e) =>
                    handleGeneralChange('companyEstablishedDate', e.target.value)
                  }
                  style={{ width: 200, marginRight: 20 }}
                />
                <span style={{ width: 50, fontWeight: 'bold' }}>Хаяг:</span>
                <Input
                  value={g.address}
                  onChange={(e) => handleGeneralChange('address', e.target.value)}
                  style={{ flex: 1, marginRight: 20 }}
                />
                <span style={{ width: 50, fontWeight: 'bold' }}>Утас:</span>
                <Input
                  value={g.phone}
                  onChange={(e) => handleGeneralChange('phone', e.target.value)}
                  style={{ width: 150 }}
                />
              </div>
            </Col>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ width: 120, fontWeight: 'bold' }}>
                  Салбартай эсэх:
                </span>
                <YesNoCheckbox field="hasBranches" />
                <span style={{ width: 70, fontWeight: 'bold' }}>
                  Салбарын тоо:
                </span>
                <Input
                  value={g.branchCount}
                  onChange={(e) => handleGeneralChange('branchCount', e.target.value)}
                  style={{ width: 100, marginLeft: 10, marginRight: 20 }}
                />
                <span style={{ width: 80, fontWeight: 'bold' }}>
                  Ажилчдын тоо:
                </span>
                <Input
                  value={g.employeeCount}
                  onChange={(e) => handleGeneralChange('employeeCount', e.target.value)}
                  style={{ width: 50 }}
                />
              </div>
            </Col>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ width: 120, fontWeight: 'bold' }}>
                  Нягтлан бодогчтой эсэх:
                </span>
                <YesNoCheckbox field="hasAccountant" />
                <span style={{ width: 80, fontWeight: 'bold' }}>
                  Нягтлан бодогч нь:
                </span>
                <AccountantCheckbox field="accountantType" />
              </div>
            </Col>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ width: 120, fontWeight: 'bold' }}>
                  Санхүүгийн программтай эсэх:
                </span>
                <YesNoCheckbox field="hasFinancialSoftware" />
                <span style={{ width: 125, fontWeight: 'bold' }}>
                  Санхүүгийн программын нэр:
                </span>
                <Input
                  value={g.financialSoftwareName}
                  onChange={(e) =>
                    handleGeneralChange('financialSoftwareName', e.target.value)
                  }
                  style={{ width: 150 }}
                />
              </div>
            </Col>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ width: 160, fontWeight: 'bold' }}>
                  Жилийн борлуулалтын орлого:
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <Checkbox
                    checked={g.annualRevenue.range1}
                    onChange={(e) =>
                      handleGeneralChange('annualRevenue', {
                        ...g.annualRevenue,
                        range1: e.target.checked,
                      })
                    }
                  >
                    50 сая - 300 сая
                  </Checkbox>
                  <Checkbox
                    checked={g.annualRevenue.range2}
                    onChange={(e) =>
                      handleGeneralChange('annualRevenue', {
                        ...g.annualRevenue,
                        range2: e.target.checked,
                      })
                    }
                  >
                    300 сая - 1.5 тэрбум
                  </Checkbox>
                  <Checkbox
                    checked={g.annualRevenue.range3}
                    onChange={(e) =>
                      handleGeneralChange('annualRevenue', {
                        ...g.annualRevenue,
                        range3: e.target.checked,
                      })
                    }
                  >
                    1.5 тэрбум - 6 тэрбум
                  </Checkbox>
                  <Checkbox
                    checked={g.annualRevenue.range4}
                    onChange={(e) =>
                      handleGeneralChange('annualRevenue', {
                        ...g.annualRevenue,
                        range4: e.target.checked,
                      })
                    }
                  >
                    6 тэрбум болон дээш
                  </Checkbox>
                </div>
              </div>
            </Col>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ width: 180, fontWeight: 'bold' }}>
                  Татварын хяналт шалгалт орсон эсэх:
                </span>
                <YesNoCheckbox field="taxInspection" />
                <span style={{ width: 220, fontWeight: 'bold', marginLeft: 20 }}>
                  Шалгалтаар зөрчил илэрсэн эсэх:
                </span>
                <YesNoCheckbox field="taxInspectionViolation" />
                <Input
                  value={g.taxInspectionDate}
                  onChange={(e) =>
                    handleGeneralChange('taxInspectionDate', e.target.value)
                  }
                  placeholder="Шалгалт орсон огноо"
                  style={{ marginLeft: 20, width: 200 }}
                />
              </div>
            </Col>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8 }}>
                <span style={{ width: 180, fontWeight: 'bold' }}>
                  Татварын хяналт шалгалтаар орсон огноо:
                </span>
                <Input
                  value={g.taxInspectionDate}
                  onChange={(e) =>
                    handleGeneralChange('taxInspectionDate', e.target.value)
                  }
                  style={{ width: 200, marginRight: 20 }}
                />
                <span style={{ width: 150, fontWeight: 'bold' }}>
                  Илэрсэн зөрчлийн мэдээлэл:
                </span>
                <TextArea
                  value={g.violationDetails}
                  onChange={(e) =>
                    handleGeneralChange('violationDetails', e.target.value)
                  }
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  style={{ flex: 1 }}
                />
              </div>
            </Col>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ width: 180, fontWeight: 'bold' }}>
                  Үйлчилгээний төрөл:
                </span>
                <Checkbox
                  checked={g.serviceType.internalAudit}
                  onChange={(e) =>
                    handleGeneralChange('serviceType', {
                      ...g.serviceType,
                      internalAudit: e.target.checked,
                    })
                  }
                  style={{ marginRight: 20 }}
                >
                  1. Санхүүгийн дотоод
                </Checkbox>
                <Checkbox
                  checked={g.serviceType.consulting}
                  onChange={(e) =>
                    handleGeneralChange('serviceType', {
                      ...g.serviceType,
                      consulting: e.target.checked,
                    })
                  }
                  style={{ marginRight: 20 }}
                >
                  2. Зөвлөх үйлчилгээ
                </Checkbox>
                <Checkbox
                  checked={g.serviceType.contractAccounting}
                  onChange={(e) =>
                    handleGeneralChange('serviceType', {
                      ...g.serviceType,
                      contractAccounting: e.target.checked,
                    })
                  }
                >
                  3. Гэрээт нягтлан
                </Checkbox>
              </div>
            </Col>
          </Row>
          <Table
            columns={table3Cols}
            dataSource={state.tables.table3}
            pagination={false}
            bordered
            size="small"
          />
          <Button onClick={addRowToTable3}>Мөр нэмэх</Button>
          <div style={{ marginTop: 20 }}>
            <Field
              label="Тэмдэглэл хөтөлсөн"
              value={g.noteTaker}
              onChange={(e) => handleGeneralChange('noteTaker', e.target.value)}
            />
          </div>
        </div>
      ),
    },
    {
      key: '1',
      label: 'ГҮЙЦЭТГЭСЭН АЖЛЫН ТАЙЛАН',
      children: (
        <div id="tab-content-1">
          <div className="header-buttons">
            <Button className="add-btn" onClick={() => handlePrint('1')}>
              Хэвлэх
            </Button>
            <Button className="add-btn" onClick={saveToLocalStorage}>
              Хадгалах
            </Button>
          </div>
          <div style={{ marginBottom: 20 }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Field
                  label="Компанийн нэр"
                  value={g.companyName}
                  onChange={(e) => handleGeneralChange('companyName', e.target.value)}
                />
              </Col>
              <Col span={24}>
                <Field
                  label="Гэрээний дугаар"
                  value={g.contractNumber}
                  onChange={(e) =>
                    handleGeneralChange('contractNumber', e.target.value)
                  }
                />
              </Col>
              <Col span={24}>
                <Field
                  label="Ажил гүйцэтгэсэн хугацаа"
                  placeholder="202.. он ... сарын ... өдөр"
                  value={g.workPeriod}
                  onChange={(e) => handleGeneralChange('workPeriod', e.target.value)}
                />
              </Col>
            </Row>
            <Table
              columns={table1Cols}
              dataSource={state.tables.table1}
              pagination={false}
              bordered
              size="small"
            />
          </div>
          <Button onClick={addRowToTable1}>Мөр нэмэх</Button>
          <div style={{ marginTop: 20 }}>
            <div style={{ marginTop: 20 }}>
              <h4 style={{ fontWeight: 'bold', fontSize: 16, paddingLeft: 10 }}>
                ТМЗ үйлчилгээний дараах алхамууд
              </h4>
            </div>
            <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
              <Col span={24}>
                <div className="field-label" style={{ fontWeight: 'bold' }}>
                  Тайлан гаргасан
                </div>
                <div className="field-value">{g.reportPreparedBy}</div>
              </Col>
              <Col span={24}>
                <Field
                  label="Татварын мэргэшсэн зөвлөх"
                  value={g.taxConsultant}
                  style={{ fontWeight: 'normal' }}
                  onChange={(e) => handleGeneralChange('taxConsultant', e.target.value)}
                />
              </Col>
              <Col span={24}>
                <div className="field-label" style={{ fontWeight: 'bold' }}>
                  Тайланг хянан баталсан
                </div>
                <div className="field-value">{g.reportReviewedBy}</div>
              </Col>
              <Col span={12}>
                <Field
                  label="Албан тушаал"
                  value={g.User1rank}
                  onChange={(e) =>
                    handleGeneralChange('User1rank', e.target.value)
                  }
                />
              </Col>
              <Col span={12}>
                <Field
                  label="Нэр"
                  value={g.User1name}
                  onChange={(e) =>
                    handleGeneralChange('User1name', e.target.value)
                  }
                />
              </Col>
              <Col span={12}>
                <Field
                  label="Албан тушаал"
                  value={g.User2rank}
                  onChange={(e) =>
                    handleGeneralChange('User2rank', e.target.value)
                  }
                />
              </Col>
              <Col span={12}>
                <Field
                  label="Нэр"
                  value={g.User2name}
                  onChange={(e) =>
                    handleGeneralChange('User2name', e.target.value)
                  }
                />
              </Col>
            </Row>
          </div>
        </div>
      ),
    },
    {
      key: '2',
      label: 'ТАТВАРЫН МЭРГЭШСЭН ЗӨВЛӨХ ҮЙЛЧИЛГЭЭНИЙ ТАЙЛАН',
      children: (
        <div id="tab-content-2">
          <div className="header-buttons">
            <Button className="add-btn" onClick={() => handlePrint('2')}>
              Хэвлэх
            </Button>
            <Button className="add-btn" onClick={saveToLocalStorage}>
              Хадгалах
            </Button>
          </div>
          <Row gutter={[16, 16]} style={{ margin: 20 }}>
            <Col span={24}>
              <Field
                label="Компанийн нэр"
                value={g.companyName}
                onChange={(e) => handleGeneralChange('companyName', e.target.value)}
              />
            </Col>
            <Col span={24}>
              <Field
                label="Огноо"
                placeholder="202.. он ... сарын ... өдөр"
                value={g.date}
                onChange={(e) => handleGeneralChange('date', e.target.value)}
              />
            </Col>
          </Row>
          <Table
            columns={table2Cols}
            dataSource={state.tables.table2}
            pagination={false}
            bordered
            size="small"
          />
          <Button onClick={addRowToTable2}>Мөр нэмэх</Button>
          <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
            <Col span={24}>
              <Field
                label="Татварын мэргэшсэн зөвлөх"
                value={g.taxConsultant}
                onChange={(e) => handleGeneralChange('taxConsultant', e.target.value)}
              />
            </Col>
            <Col span={24}>
              <Field
                label="ТМЗ код"
                value={g.taxConsultantCode}
                onChange={(e) =>
                  handleGeneralChange('taxConsultantCode', e.target.value)
                }
              />
            </Col>
            <Col span={24}>
              <div className="field-label" style={{ fontWeight: 'bold' }}>
                Хүлээн авсан
              </div>
              <div className="field-value">{g.reportReviewedBy}</div>
            </Col>
            <Col span={12}>
              <Field
                label="Албан тушаал"
                value={g.User1rank}
                onChange={(e) =>
                  handleGeneralChange('User1rank', e.target.value)
                }
              />
            </Col>
            <Col span={12}>
              <Field
                label="Нэр"
                value={g.User1name}
                onChange={(e) =>
                  handleGeneralChange('User1name', e.target.value)
                }
              />
            </Col>
            <Col span={12}>
              <Field
                label="Албан тушаал"
                value={g.User2rank}
                onChange={(e) =>
                  handleGeneralChange('User2rank', e.target.value)
                }
              />
            </Col>
            <Col span={12}>
              <Field
                label="Нэр"
                value={g.User2name}
                onChange={(e) =>
                  handleGeneralChange('User2name', e.target.value)
                }
              />
            </Col>
          </Row>
        </div>
      ),
    },
  ];

  return (
    <div className="tabbed-tables" style={{ padding: '20px' }}>
      <Tabs activeKey={activeTab} items={tabItems} onChange={onTabChange} />
      <Modal
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Хадгалах and Switch"
        cancelText="Switch without Saving"
        title="Unsaved Changes"
      >
        <p>Та энэ табаас гарахаасаа өмнө өөрчлөлтөө хадгалахыг хүсэж байна уу?</p>
      </Modal>
    </div>
  );
}